import { useContext, useState, useEffect } from 'react'
import { baseURL } from '../config'
import axios from 'axios'
import AuthContext from '../Context/AuthContext'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router'
import { MachineOrderFormValidation, getOrderErrorScrollTarget } from '../utils/MachineOrderFormValidation'
import UseEditTemplate from '../Components/UseEditTemplate'
import { pickModelDescription } from '../utils/orderModelDescription'
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChanges'

function MachineOrderUseTemplate() {
  const [fetchingTemplateDetails, setFetchingTemplateDetails] = useState(true)
  const [templateDetails, setTemplateDetails] = useState({})
  const [originalTemplateDetails, setOriginalTemplateDetails] = useState({})
  const { token } = useContext(AuthContext);
  const [templateModelDesc, setTemplateModelDesc] = useState()
  const [selectedAssembly, setSelectedAssembly] = useState([])
  const [originalSelectedAssembly, setOriginalSelectedAssembly] = useState([])
  const { assembly_id } = useParams();
  const [initialFormSubmit, setInitialFormSubmit] = useState(false)
  const [assignedQR, setAssignedQR] = useState()
  const [qr, setQR] = useState('')
  const [accountNumberOptions, setAccountNumberOptions] = useState([])
  const [savingMachineOrders, setSavingMachineOrders] = useState(false)
  const navigate = useNavigate()
  // The template's main assemblies; exactly one of them is chosen for the order
  const [mainOptions, setMainOptions] = useState([])
  const [selectedMainId, setSelectedMainId] = useState(null)

  // Detect buyout mode — no template lookup needed
  const isBuyoutMode = assembly_id === 'buyout'

  const originalCustomerData = {
    account_number: '',
    name: '',
    company_name: '',
    primary_contact: '',
    primary_email: '',
    accounting_contact: '',
    accounting_email: '',
    bill_to: '',
    ship_to: '',
    others: '',
    shipment_date: '',
  }
  const originalModsData = {
    sales_order_number: '',
    serial_number: '',
    customer_po: '',
  }

  const [mods_data, setMods_data] = useState({
    sales_order_number: '',
    serial_number: '',
    customer_po: '',
  });
  const [customer_data, setCustomer_data] = useState({
    account_number: '',
    name: '',
    company_name: '',
    primary_contact: '',
    primary_email: '',
    accounting_contact: '',
    accounting_email: '',
    bill_to: '',
    ship_to: '',
    others: '',
    shipment_date: '',
  });
  const [validateFields, setValidateFields] = useState({
    qr: '',
    model_number: '',
    model_description: '',
    sales_order_number: '',
    serial_number: '',
    customer_po: '',
    account_number: '',
    name: '',
    company_name: '',
    primary_contact: '',
    accounting_contact: '',
    primary_email: '',
    accounting_email: '',
    bill_to: '',
    ship_to: '',
    shipment_date: '',
    others: '',
  });

  // Everything the user can change on this page, compared against how it looked once the template loaded
  const { allowNavigation } = useUnsavedChangesGuard({
    mods_data,
    customer_data,
    qr: qr || '',
    selectedAssembly,
    selectedMainId,
    subAssemblyIds: Object.values(templateDetails).flat().map((item) => item._id),
  }, !fetchingTemplateDetails)

  const saveMachineOrder = () => {
    setInitialFormSubmit(true)
    const [isAnyEmptyFields, validateFieldsCopy] = MachineOrderFormValidation(qr, mods_data, customer_data, validateFields, undefined, !isBuyoutMode);
    setValidateFields(validateFieldsCopy)
    if (isAnyEmptyFields) {
      const tableElement = document.getElementById(getOrderErrorScrollTarget(validateFieldsCopy));
      tableElement.scrollIntoView({ behavior: "smooth" });
    } else {
      const createMODS = async () => {
        try {
          setSavingMachineOrders(true)
          const response = await axios.post(`${baseURL}/mods/addMods`,
            {
              qr: qr,
              mods_data: mods_data,
              customer_data: customer_data,
              // Send "buyout" as the template_id so the backend can handle it
              template_id: isBuyoutMode ? "buyout" : assembly_id,
              // The chosen main assembly is saved first, followed by the selected sub assemblies
              ...(!isBuyoutMode && selectedMainId ? { main_assembly: selectedMainId } : {}),
              assemblies: !isBuyoutMode && selectedMainId ? [selectedMainId, ...selectedAssembly] : selectedAssembly
            },
            {
              headers: {
                "ngrok-skip-browser-warning": "any",
                "x-access-token": token
              }
            }
          );
          if (response.status === 201) {
            toast.success('Machine Order Created Successfully')
            allowNavigation()
            navigate('/machine_orders')
          }
        } catch (error) {
          toast.error('Something Went Wrong, Template Not Saved')
        } finally {
          setSavingMachineOrders(false)
        }
      }
      createMODS()
    }
  }

  useEffect(() => {
    if (isBuyoutMode) {
      // Buyout mode: skip template fetch, set generic title, start with empty assemblies
      setTemplateModelDesc({ name: 'Buyout', description: 'Individual equipment buyout' })
      setTemplateDetails({})
      setOriginalTemplateDetails({})
      setSelectedAssembly([])
      setOriginalSelectedAssembly([])
      setFetchingTemplateDetails(false)
      return
    }

    // Only the latest request is applied (React StrictMode runs this effect twice in dev), so a
    // slower duplicate response can't overwrite what was typed or chosen in the meantime.
    let ignore = false;
    const fetchData = async () => {
      try {
        setFetchingTemplateDetails(true);
        const response = await axios.get(`${baseURL}/template/getTemplateById/${assembly_id}`, {
          headers: {
            "ngrok-skip-browser-warning": "any",
            "Content-Type": "multipart/form-data",
            "x-access-token": token
          },
          params: {
            assembly_id: assembly_id
          }
        });
        if (ignore) return;
        const templateDesc = response.data.data.templateDesc
        setTemplateModelDesc(templateDesc);
        // The template's main assemblies: one of them is chosen for the order (the first by default).
        // The list below holds the sub assemblies, none selected until ticked.
        const mains = response.data.data.mainAssemblies || []
        setMainOptions(mains)
        setSelectedMainId(mains[0]?._id || null)
        // Model # starts as the template's. The model description starts as the one of the chosen main
        // assembly (the template's only if that has none). Both can be edited for this order.
        setMods_data(prev => ({
          ...prev,
          model_number: templateDesc.name || '',
          model_description: pickModelDescription(mains[0], templateDesc.model_description),
        }))
        let _assemblyData = response.data.data.templateData
        setOriginalTemplateDetails(_assemblyData);
        setTemplateDetails(_assemblyData);
        setSelectedAssembly([])
        setOriginalSelectedAssembly([])
      } catch (error) {
        console.error('Error:', error);
      } finally {
        if (!ignore) setFetchingTemplateDetails(false);
      }
    };

    fetchData();
    return () => {
      ignore = true;
    };
  }, [assembly_id, token, isBuyoutMode]);

  useEffect(() => {
    if (initialFormSubmit) {
      setValidateFields(prev => {
        const result = MachineOrderFormValidation(qr, mods_data, customer_data, prev, undefined, !isBuyoutMode);
        return (result[1])
      })
    }
  }, [qr, mods_data, customer_data, initialFormSubmit, isBuyoutMode]);

  return (
    <UseEditTemplate
      savingMachineOrders={savingMachineOrders}
      originalSelectedAssembly={originalSelectedAssembly}
      templateModelDesc={templateModelDesc}
      templateDetails={templateDetails}
      setTemplateDetails={setTemplateDetails}
      originalTemplateDetails={originalTemplateDetails}
      fetchingTemplateDetails={fetchingTemplateDetails}
      accountNumberOptions={accountNumberOptions}
      setAccountNumberOptions={setAccountNumberOptions}
      mods_data={mods_data}
      setMods_data={setMods_data}
      customer_data={customer_data}
      setCustomer_data={setCustomer_data}
      assignedQR={assignedQR}
      setAssignedQR={setAssignedQR}
      validateFields={validateFields}
      saveChanges={saveMachineOrder}
      selectedAssembly={selectedAssembly}
      setSelectedAssembly={setSelectedAssembly}
      originalCustomerData={originalCustomerData}
      originalModsData={originalModsData}
      qr={qr}
      setQR={setQR}
      mainOptions={mainOptions}
      selectedMainId={selectedMainId}
      setSelectedMainId={setSelectedMainId}
      editOrder={false}
      assemblyFilter={isBuyoutMode ? 'Buyout' : null}
    />
  )
}

export default MachineOrderUseTemplate